import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, patch, post, put, type LedgerEntry, type Pricing } from "../../lib/api";
import { formatDate, formatNumber } from "../../lib/format";
import { Alert, Badge, Button, Card, Field, Input, Loading, PageHeader, Stat, StatusBadge, Table, Tabs, Td, Textarea } from "../../components/ui";

type Tab = "overview" | "workspaces" | "partners" | "users" | "runs" | "audit" | "requests" | "wallets" | "pricebook" | "outbox";

/** Platform admin console (ADM-1..5). The API enforces admin + MFA. */
export function AdminPage() {
  const [tab, setTab] = useState<Tab>("overview");
  return (
    <>
      <PageHeader title="Platform admin" subtitle="Cloud Migration Hub staff only. Every action here is audited." />
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "overview", label: "Overview" },
          { value: "workspaces", label: "Workspaces" },
          { value: "partners", label: "Partners" },
          { value: "users", label: "Users" },
          { value: "runs", label: "Runs" },
          { value: "wallets", label: "Wallets" },
          { value: "pricebook", label: "Price book" },
          { value: "requests", label: "Access requests" },
          { value: "audit", label: "Audit log" },
          { value: "outbox", label: "Unsent email" },
        ]}
      />
      {tab === "overview" && <Overview />}
      {tab === "workspaces" && <SearchList kind="workspaces" />}
      {tab === "partners" && <Partners />}
      {tab === "users" && <SearchList kind="users" />}
      {tab === "runs" && <Runs />}
      {tab === "wallets" && <Wallets />}
      {tab === "pricebook" && <PriceBookEditor />}
      {tab === "requests" && <Requests />}
      {tab === "audit" && <SearchList kind="audit" />}
      {tab === "outbox" && <Outbox />}
    </>
  );
}

function Overview() {
  const q = useQuery({ queryKey: ["admin", "overview"], queryFn: () => get<Record<string, number>>("/api/admin/overview") });
  if (q.isLoading) return <Loading />;
  if (q.error) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const d = q.data!;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
      <Stat label="Users" value={formatNumber(d.users)} />
      <Stat label="Partners" value={formatNumber(d.partners)} />
      <Stat label="Workspaces" value={formatNumber(d.workspaces)} />
      <Stat label="Runs (24h)" value={formatNumber(d.runs24h)} />
      <Stat label="Failed runs (24h)" value={formatNumber(d.failedRuns24h)} />
      <Stat label="Access requests" value={formatNumber(d.accessRequests)} />
    </div>
  );
}

function SearchList({ kind }: { kind: "workspaces" | "users" | "audit" }) {
  const [q, setQ] = useState("");
  const data = useQuery({ queryKey: ["admin", kind, q], queryFn: () => get<Record<string, Array<Record<string, unknown>>>>(`/api/admin/${kind}?q=${encodeURIComponent(q)}`) });
  const rows = data.data ? (Object.values(data.data)[0] ?? []) : [];
  const cols: Record<string, string[]> = {
    workspaces: ["id", "name", "partner_name", "region", "retention", "members", "created_at"],
    users: ["email", "name", "is_platform_admin", "email_verified_at", "mfa_enabled_at", "created_at"],
    audit: ["at", "actor_email", "action", "workspace_id", "partner_id", "target", "details"],
  };
  const fmt = (k: string, v: unknown) => (k.endsWith("_at") || k === "at" ? formatDate(v as number) : v === null || v === undefined ? "—" : String(v));
  return (
    <Card actions={<Input aria-label="Search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" />}>
      {data.isLoading ? (
        <Loading />
      ) : (
        <Table head={cols[kind]!}>
          {rows.map((r, i) => (
            <tr key={i}>
              {cols[kind]!.map((k) => (
                <Td key={k} className={k === "id" || k === "details" || k === "target" ? "max-w-xs truncate font-mono text-xs" : "text-sm"}>
                  {fmt(k, r[k])}
                </Td>
              ))}
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

function Partners() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ["admin", "partners"], queryFn: () => get<{ partners: Array<{ id: string; name: string; wholesale_discount_pct: number | null; workspaces: number; created_at: number }> }>("/api/admin/partners") });
  const [msg, setMsg] = useState<string | null>(null);
  if (data.isLoading) return <Loading />;
  return (
    <Card>
      {msg && <Alert tone="danger">{msg}</Alert>}
      <Table head={["Partner", "ID", "Clients", "Wholesale discount", "Created"]}>
        {data.data!.partners.map((p) => (
          <tr key={p.id}>
            <Td className="font-medium">{p.name}</Td>
            <Td className="font-mono text-xs">{p.id}</Td>
            <Td>{p.workspaces}</Td>
            <Td>
              <Input
                aria-label={`Discount for ${p.name}`}
                type="number"
                className="w-24"
                defaultValue={p.wholesale_discount_pct ?? ""}
                placeholder="default"
                onBlur={async (e) => {
                  try {
                    await patch(`/api/admin/partners/${p.id}`, { wholesaleDiscountPct: e.target.value === "" ? null : Number(e.target.value) });
                    qc.invalidateQueries({ queryKey: ["admin", "partners"] });
                  } catch (err) {
                    setMsg(errorMessage(err));
                  }
                }}
              />
            </Td>
            <Td className="muted">{formatDate(p.created_at)}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

function Runs() {
  const [status, setStatus] = useState("");
  const data = useQuery({ queryKey: ["admin", "runs", status], queryFn: () => get<{ runs: Array<{ id: string; workspace_name: string; kind: string; status: string; records: number; credits_used: number; created_at: number }> }>(`/api/admin/runs${status ? `?status=${status}` : ""}`) });
  return (
    <Card
      actions={
        <select className="rounded-lg border border-default bg-[var(--surface)] px-2 py-1 text-sm" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>
          {["queued", "running", "completed", "failed", "cancelled"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      }
    >
      {data.isLoading ? (
        <Loading />
      ) : (
        <Table head={["Run", "Workspace", "Kind", "Status", "Records", "Credits", "Created"]}>
          {data.data!.runs.map((r) => (
            <tr key={r.id}>
              <Td className="font-mono text-xs">{r.id}</Td>
              <Td>{r.workspace_name}</Td>
              <Td>{r.kind}</Td>
              <Td>
                <StatusBadge status={r.status} />
              </Td>
              <Td className="tabular">{formatNumber(r.records)}</Td>
              <Td className="tabular">{formatNumber(r.credits_used)}</Td>
              <Td className="muted">{formatDate(r.created_at)}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

function Wallets() {
  const [walletId, setWalletId] = useState("");
  const [loaded, setLoaded] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  const qc = useQueryClient();
  const w = useQuery({ queryKey: ["admin", "wallet", loaded], enabled: !!loaded, queryFn: () => get<{ balance: number; ledger: LedgerEntry[] }>(`/api/admin/wallets/${encodeURIComponent(loaded!)}`) });
  return (
    <>
      <Card title="Find a wallet" className="mb-6">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setLoaded(walletId.trim());
          }}
        >
          <Input aria-label="Wallet id" placeholder="ws:ws_… or partner:ptn_…" value={walletId} onChange={(e) => setWalletId(e.target.value)} className="max-w-md" />
          <Button type="submit">Load</Button>
        </form>
      </Card>
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      {loaded && w.data && (
        <Card title={`${loaded} · balance ${formatNumber(w.data.balance)}`}>
          <form
            className="mb-6 grid gap-3 sm:grid-cols-[140px_1fr_auto]"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const r = await post<{ balance: number }>(`/api/admin/wallets/${encodeURIComponent(loaded)}/adjust`, { amount: Number(amount), reason });
                setMsg({ tone: "ok", text: `Adjusted. New balance ${formatNumber(r.balance)}.` });
                setAmount("");
                setReason("");
                qc.invalidateQueries({ queryKey: ["admin", "wallet", loaded] });
              } catch (err) {
                setMsg({ tone: "danger", text: errorMessage(err) });
              }
            }}
          >
            <Input type="number" aria-label="Amount" placeholder="± credits" required value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Input aria-label="Reason" placeholder="Reason (required, shown in the ledger)" required minLength={5} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button type="submit">Adjust</Button>
          </form>
          <Table head={["When", "Type", "Description", "Amount", "Balance"]}>
            {w.data.ledger.map((l) => (
              <tr key={l.id}>
                <Td className="muted">{formatDate(l.at)}</Td>
                <Td>
                  <Badge>{l.type}</Badge>
                </Td>
                <Td>{l.description}</Td>
                <Td className="tabular">{formatNumber(l.amount)}</Td>
                <Td className="tabular">{formatNumber(l.balance_after)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </>
  );
}

function PriceBookEditor() {
  const q = useQuery({ queryKey: ["admin", "pricebook"], queryFn: () => get<Pricing>("/api/admin/pricebook") });
  const [text, setText] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  if (q.isLoading) return <Loading />;
  const value = text ?? JSON.stringify(q.data, null, 2);
  return (
    <Card title="Price book" actions={<Badge tone="warn">{q.data?.placeholder ? "placeholder prices" : "confirmed"}</Badge>}>
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <p className="muted mb-3 text-sm">Rates are credits per 1,000 records (1 credit = US$0.01). Prices are in US cents. Set "placeholder" to false once the cost model is confirmed.</p>
      <Field label="JSON" htmlFor="pb">
        <Textarea id="pb" rows={24} value={value} onChange={(e) => setText(e.target.value)} />
      </Field>
      <Button
        onClick={async () => {
          try {
            await put("/api/admin/pricebook", JSON.parse(value));
            setMsg({ tone: "ok", text: "Price book saved." });
          } catch (err) {
            setMsg({ tone: "danger", text: err instanceof SyntaxError ? "That isn't valid JSON." : errorMessage(err) });
          }
        }}
      >
        Save price book
      </Button>
    </Card>
  );
}

function Requests() {
  const q = useQuery({ queryKey: ["admin", "requests"], queryFn: () => get<{ requests: Array<{ id: string; email: string; name: string | null; company: string | null; role: string | null; message: string | null; created_at: number }> }>("/api/admin/access-requests") });
  if (q.isLoading) return <Loading />;
  return (
    <Card>
      <Table head={["When", "Email", "Name", "Company", "Role", "Message"]}>
        {q.data!.requests.map((r) => (
          <tr key={r.id}>
            <Td className="muted whitespace-nowrap">{formatDate(r.created_at)}</Td>
            <Td>{r.email}</Td>
            <Td>{r.name ?? "—"}</Td>
            <Td>{r.company ?? "—"}</Td>
            <Td>{r.role ?? "—"}</Td>
            <Td className="max-w-sm text-sm">{r.message ?? ""}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

function Outbox() {
  const q = useQuery({ queryKey: ["admin", "outbox"], queryFn: () => get<{ sendingConfigured: boolean; messages: Array<{ id: string; to_addr: string; subject: string; text: string; created_at: number }> }>("/api/admin/outbox") });
  if (q.isLoading) return <Loading />;
  return (
    <Card title="Unsent email">
      {!q.data!.sendingConfigured && <Alert tone="warn">Email sending isn't configured in this environment, so messages are held here. Share verification or invite links with the recipient directly.</Alert>}
      {q.data!.messages.map((m) => (
        <div key={m.id} className="border-default mb-3 rounded-lg border p-3">
          <div className="text-sm font-medium">{m.subject}</div>
          <div className="muted mb-2 text-xs">
            To {m.to_addr} · {formatDate(m.created_at)}
          </div>
          <pre className="text-xs whitespace-pre-wrap">{m.text}</pre>
        </div>
      ))}
      {!q.data!.messages.length && <p className="muted text-sm">No held messages.</p>}
    </Card>
  );
}
